import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { useProjetoImpressaoFilters } from "../hooks/Impressao_listasolicitar/filter";
import { useProjetoImpressaoPagination } from "../hooks/Impressao_listasolicitar/pagination";
import { ProjetoImpressaoLista } from "../../domain/models/impressao_projeto_lista";

type LoadProjetoImpressaoState = {
  idsProjeto?: number[];
  idsCentroCusto?: number[];
  idsSolicitante?: number[];
  status?: number;
  statusEmail?: number;
  dataDe?: Date | null;
  dataAte?: Date | null;

  page?: number | null;
  rowsPerPage?: number | null;
};

export const useImpressaoListaSolicitarViewModel = () => {

  const {
    projetos: {
      getAllProjetoImpressaoUseCase,
    },

    commons: {
      getCentroCustoDropDownUseCase,
      getFuncionariosDropDownUseCase,
      getProjetosDropDownUseCase,
    },

  } = useDependencyInjection();

  const { pushErrorNotification } = usePageLoader();
  const filters = useProjetoImpressaoFilters();
  const pagination = useProjetoImpressaoPagination();
  const debouncedLoad = useCreateDebounceHandler(750);

  const [listaImpressao, setListaImpressao] = useState<ProjetoImpressaoLista[]>();
  const [loading, setLoading] = useState(false);

  const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>([]);
  const [ddlProjeto, setDdlProjeto] = useState<DropDown[]>([]);
  const [ddlFuncionario, setDdlFuncionario] = useState<DropDown[]>([]);

  const [ddlStatus, setDdlStatus] = useState<DropDown[]>([]);
  const [ddlStatusEmail, setDdlStatusEmail] = useState<DropDown[]>([]);

  const backOrNavigate = useBackOrNavigate();

  const statusOptions = [
    { title: "Todas", id: 3 },
    { title: "Aberta", id: 0 },
    { title: "Em análise", id: 1 },
    { title: "Encerrada", id: 2 },
  ];

  const statusEmailOptions = [
    { title: "Todas", id: 2 },
    { title: "Não enviados", id: 0 },
    { title: "Enviados", id: 1 },

  ];


  //#region  CARREGAMENTO

  const loadAllProjetoImpressaoLista = useCallback(
    async (funcState?: LoadProjetoImpressaoState) => {
      try {
        const filteredIdsCentroCusto = evaluateParamValue(funcState?.idsCentroCusto, filters.idsCentroCusto);
        const filteredIdsProjeto = evaluateParamValue(funcState?.idsProjeto, filters.idsProjeto);
        const filteredIdsSolicitante = evaluateParamValue(funcState?.idsSolicitante, filters.idsSolicitante);
        const filteredStatus = evaluateParamValue(funcState?.status, filters.status);
        const filteredStatusEmail = evaluateParamValue(funcState?.statusEmail, filters.statusEmail);
        const filteredDataDe = evaluateParamValue(funcState?.dataDe, filters.dataDe);
        const filteredDataAte = evaluateParamValue(funcState?.dataAte, filters.dataAte);

        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllProjetoImpressaoUseCase.execute(
          filteredIdsProjeto ?? [],
          filteredIdsCentroCusto ?? [],
          filteredIdsSolicitante ?? [],
          filteredStatus ?? 3,
          filteredStatusEmail ?? 2,
          filteredDataDe ?? undefined,
          filteredDataAte ?? undefined,
          filteredPage,
          filteredRowsPerPage,

        );

        setListaImpressao(getAllProjetoImpressaoUseCase.items);

        pagination.setRowsPerPage(getAllProjetoImpressaoUseCase.pageSize);
        pagination.setTotalItems(getAllProjetoImpressaoUseCase.totalItems);
      } catch (e: unknown) {
        setListaImpressao([]);
        pushErrorNotification("Error", processError(e, "Falha ao buscar lista de projetos!"));
      }
    },
    [
      filters,
      pagination,
      getAllProjetoImpressaoUseCase,
      pushErrorNotification
    ],
  );

  const loadCentroCustoDropDown = useCallback(async () => {
    try {
      const response = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      setDdlCentroCusto(response);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Centro de Custo!"));
    }
  }, [getCentroCustoDropDownUseCase, pushErrorNotification]);


  const loadFuncionarioDropDown = useCallback(async () => {
    try {
      const response = await getFuncionariosDropDownUseCase.getFuncionariosDropDown();
      setDdlFuncionario(response.map((f) => ({ id: f.idFuncionario, title: f.nome })));
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Funcionários!"));
    }
  }, [getFuncionariosDropDownUseCase, pushErrorNotification]);


  const loadProjetosDropDown = useCallback(async () => {
    try {
      const response = await getProjetosDropDownUseCase.execute();
      setDdlProjeto(response);

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Projetos!"));
    }
  }, [getProjetosDropDownUseCase, pushErrorNotification]);

  //#endregion


  //#region PAGE START
  usePageStartBootstrap(async () => {
    try {
      await loadAllProjetoImpressaoLista();
      await loadCentroCustoDropDown();
      await loadFuncionarioDropDown();
      await loadProjetosDropDown();
      setDdlStatus(statusOptions);
      setDdlStatusEmail(statusEmailOptions);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados!"));
    }
  });

  //#endregion


  //#region  PAGINAÇÃO
  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        await loadAllProjetoImpressaoLista({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao paginar!"));
      } finally {
        setLoading(false);
      }
    },
    [
      loadAllProjetoImpressaoLista,
      pushErrorNotification
    ],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        await loadAllProjetoImpressaoLista({ page: 1, rowsPerPage: rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao paginar!"));
      } finally {
        setLoading(false);
      }
    },
    [
      loadAllProjetoImpressaoLista,
      pushErrorNotification
    ],
  );

  //#endregion



  //#region  PESQUISA

  const handleProjetoChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setIdsProjeto(newValue);
      return async () => await loadAllProjetoImpressaoLista({ idsProjeto: newValue });
    },
    [filters, loadAllProjetoImpressaoLista],
  );

  const handleCentroCustoChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setIdsCentroCusto(newValue);
      return async () => await loadAllProjetoImpressaoLista({ idsCentroCusto: newValue });
    },
    [filters, loadAllProjetoImpressaoLista],
  );

  const handleSolicitanteChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setIdsSolicitante(newValue);
      return async () => await loadAllProjetoImpressaoLista({ idsSolicitante: newValue });
    },
    [filters, loadAllProjetoImpressaoLista],
  );

  const handleStatusChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number) => {
      filters.setStatus(newValue);
      return async () => await loadAllProjetoImpressaoLista({ status: newValue });
    },
    [filters, loadAllProjetoImpressaoLista],
  );

  const handleStatusEmailChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number) => {
      filters.setStatusEmail(newValue);
      return async () => await loadAllProjetoImpressaoLista({ statusEmail: newValue });
    },
    [filters, loadAllProjetoImpressaoLista],
  );

  const handleDataChange = useDebouncedCallback(
    debouncedLoad,
    (de: Date | null, ate: Date | null) => {
      filters.setPeriodo(de, ate);
      return async () => await loadAllProjetoImpressaoLista({ dataDe: de, dataAte: ate });
    },
    [filters, loadAllProjetoImpressaoLista],
  );

  const handleClear = useCallback(async () => {
    filters.clear();
    await loadAllProjetoImpressaoLista({
      idsProjeto: [],
      idsCentroCusto: [],
      idsSolicitante: [],
      status: 3,
      statusEmail: 2,
      dataDe: null,
      dataAte: null,

      page: 1,
      rowsPerPage: pagination.rowsPerPage,
    });
  }, [
    filters,
    loadAllProjetoImpressaoLista,
    pagination.rowsPerPage]);

  //#endregion

  //#region CRIAÇÃO E EDIÇÃO

  const handleEdit = useCallback(
    (id: number | null, openState: boolean) => {
      if (!openState) window.open(`/Impressao_cadastrosolicitar?id=${id}&visualizar=false`, "_blank", "noopener,noreferrer");
      else backOrNavigate(`/Impressao_cadastrosolicitar?id=${id}&visualizar=false`);
    },
    [backOrNavigate],
  );

  const handleVisualizar = useCallback(
    (id: number | null, openState: boolean) => {
      if (!openState) window.open(`/Impressao_cadastrosolicitar?id=${id}&visualizar=true`, "_blank", "noopener,noreferrer");
      else backOrNavigate(`/Impressao_cadastrosolicitar?id=${id}&visualizar=true`);
    },
    [backOrNavigate],
  );

  const handleCreate = useCallback(() => backOrNavigate(`/Impressao_cadastrosolicitar?visualizar=false`), [backOrNavigate]);

  //#endregion

  return {
    listaImpressao,
    filters,
    pagination,
    loading,
    ddlCentroCusto: ddlCentroCusto ?? [],
    ddlProjeto: ddlProjeto ?? [],
    ddlFuncionario: ddlFuncionario ?? [],
    ddlStatus: ddlStatus ?? [],
    ddlStatusEmail: ddlStatusEmail ?? [],

    loadAllProjetoImpressaoLista,
    handlePageChange,
    handleRowsPerPageChange,
    handleClear,
    handleProjetoChange,
    handleCentroCustoChange,
    handleSolicitanteChange,
    handleStatusChange,
    handleStatusEmailChange,
    handleDataChange,
    handleVisualizar,
    handleCreate,
    handleEdit,
  };
};
